import React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Input } from "@replo/design-system/components/input/Input";
import twMerge from "@replo/design-system/utils/twMerge";

interface OnboardingShopInfoProps {
  shopName: string;
  setShopName: (value: string) => void;
  isBusinessContext?: boolean;
  isShopifySelected: boolean | null;
  onShopifySelectedChange: (value: boolean) => void;
  isShopifyPaymentProcessor?: boolean;
}

const OnboardingShopInfo: React.FC<OnboardingShopInfoProps> = ({
  shopName,
  setShopName,
  isBusinessContext = true,
  isShopifySelected,
  onShopifySelectedChange,
  isShopifyPaymentProcessor = false,
}) => {
  // NOTE: We need this state because we don't want to display the black bg when the image is loaded in
  // This is because the image has a fade in/out animation
  const [isBackgroundImageLoaded, setIsBackgroundImageLoaded] =
    React.useState(false);

  return (
    <div className="flex flex-col gap-8">
      <LabeledControl
        label={
          isBusinessContext
            ? "What's the name of your business?"
            : "What's the name of your client's business?"
        }
        size="base"
      >
        <Input
          type="text"
          value={shopName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            return setShopName(e.target.value);
          }}
          size="base"
          placeholder="Enter your business's name"
        />
      </LabeledControl>
      <LabeledControl
        label={
          isBusinessContext
            ? "Does your business use Shopify?"
            : "Does your client's business use Shopify?"
        }
        size="base"
      >
        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          <div className="md:flex-1">
            <div
              className={twMerge(
                "h-50 md:h-50 border rounded-xl p-6 group cursor-pointer overflow-hidden relative transition-colors duration-300",
                isShopifySelected === false && !isBackgroundImageLoaded
                  ? "bg-black"
                  : "border-border",
              )}
              onClick={() => onShopifySelectedChange(false)}
            >
              <img
                src="/images/onboarding/replo-v2-bg.svg"
                alt=""
                onLoad={() => setIsBackgroundImageLoaded(true)}
                className={twMerge(
                  "absolute inset-0 w-full h-full object-cover transition-opacity duration-300 scale-110",
                  isShopifySelected === false ? "opacity-100" : "opacity-0",
                )}
              />

              <div
                className={twMerge(
                  "absolute transition-all duration-500 bg-no-repeat bg-contain bg-center transform group-hover:-translate-x-6",
                  isShopifySelected === false
                    ? "-bottom-17 w-80 h-80 -right-40 md:-right-25"
                    : "-bottom-17 w-80 h-80 -right-40 md:-right-25",
                )}
                style={{
                  backgroundImage: `url(${
                    isShopifySelected === false
                      ? "/images/onboarding/storefront-color.png"
                      : "/images/onboarding/storefront-bw.png"
                  })`,
                }}
              />

              <div className="flex flex-col justify-center h-full relative z-10">
                <div className="flex flex-col gap-1 max-w-[60%] pr-4">
                  <div
                    className={twMerge(
                      "text-lg font-medium font-editorial transition-colors duration-300",
                      isShopifySelected === false && "text-brand-seashell",
                    )}
                  >
                    {isShopifyPaymentProcessor ? "Connect Shopify later" : "No"}
                  </div>
                  <div
                    className={twMerge(
                      "font-light transition-colors duration-300",
                      isShopifySelected === false &&
                        "text-placeholder text-brand-seashell",
                    )}
                  >
                    {isShopifyPaymentProcessor
                      ? "You can add our Shopify integration later from settings."
                      : "I'll sell directly through Replo or connect an existing Shopify store later."}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:flex-1">
            <div
              className={twMerge(
                "h-50 md:h-50 border rounded-xl p-6 group cursor-pointer overflow-hidden relative transition-colors duration-300",
                isShopifySelected === true && !isBackgroundImageLoaded
                  ? "bg-black"
                  : "border-border",
              )}
              onClick={() => onShopifySelectedChange(true)}
            >
              <img
                src="/images/onboarding/replo-v2-bg.svg"
                alt=""
                onLoad={() => setIsBackgroundImageLoaded(true)}
                className={twMerge(
                  "absolute inset-0 w-full h-full object-cover transition-opacity duration-300 scale-110",
                  isShopifySelected === true ? "opacity-100" : "opacity-0",
                )}
              />
              <div
                className={twMerge(
                  "absolute transition-all duration-500 bg-no-repeat bg-contain bg-center transform group-hover:-translate-x-6",
                  isShopifySelected === true
                    ? "-bottom-17 w-80 h-80 -right-29"
                    : "-bottom-17 w-80 h-80 -right-29",
                )}
                style={{
                  backgroundImage: `url(${
                    isShopifySelected === true
                      ? "/images/onboarding/shopify-circle-color.svg"
                      : "/images/onboarding/shopify-circle.svg"
                  })`,
                }}
              />
              <div className="flex flex-col justify-center gap-6 h-full relative z-10">
                <div className="flex flex-col gap-1 pr-4 max-w-[60%]">
                  <div
                    className={twMerge(
                      "text-lg font-medium font-editorial transition-colors duration-300",
                      isShopifySelected === true && "text-brand-seashell",
                    )}
                  >
                    {isShopifyPaymentProcessor ? "Connect Shopify now" : "Yes"}
                  </div>
                  <div
                    className={twMerge(
                      "font-light transition-colors duration-300",
                      isShopifySelected === true &&
                        "text-placeholder text-brand-seashell",
                    )}
                  >
                    {isShopifyPaymentProcessor
                      ? "We'll take you to Shopify to connect your store."
                      : "I have an existing Shopify store I use for my business."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LabeledControl>
    </div>
  );
};

export default OnboardingShopInfo;
